import { useLayoutEffect, useRef } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { useRetroMode } from '../context/RetroModeContext'

gsap.registerPlugin(ScrollTrigger)

const prefersReducedMotion = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

// Attach the returned ref to a wrapper element. Direct children marked with
// data-reveal-item are animated individually (with stagger); otherwise the
// wrapper itself is animated as a single block. Each element tilts in from
// a 3D angle (CSS transforms only, no WebGL) so every card/box on the site
// gets its own scroll-driven 3D entrance, not just the section as a whole.
export const useScrollReveal = ({
  y = 50,
  delay = 0,
  duration = 0.9,
  stagger = 0.14,
  start = 'top 85%',
  rotateX = 24,
  scale = 0.92,
} = {}) => {
  const ref = useRef(null)
  const { retroMode } = useRetroMode()

  useLayoutEffect(() => {
    const root = ref.current
    if (!root) return undefined

    const items = root.querySelectorAll('[data-reveal-item]')
    const elements = items.length ? items : [root]

    if (retroMode || prefersReducedMotion()) {
      gsap.set(elements, {
        opacity: 1,
        y: 0,
        rotateX: 0,
        scale: 1,
        clearProps: 'transform,transformPerspective,transformOrigin',
      })
      return undefined
    }

    const ctx = gsap.context(() => {
      gsap.set(elements, {
        transformPerspective: 700,
        transformOrigin: '50% 100%',
      })
      gsap.fromTo(
        elements,
        { opacity: 0, y, rotateX: -rotateX, scale },
        {
          opacity: 1,
          y: 0,
          rotateX: 0,
          scale: 1,
          duration,
          delay,
          stagger,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: root,
            start,
            once: true,
          },
        },
      )
    }, root)

    const raf = requestAnimationFrame(() => ScrollTrigger.refresh())

    return () => {
      cancelAnimationFrame(raf)
      ctx.revert()
    }
  }, [retroMode, y, delay, duration, stagger, start, rotateX, scale])

  return ref
}
